import { useCallback, useEffect } from 'react';

import { GobblerNOFWebOnbPageView } from '@groww-tech-pvt/gobbler-proto-ts/dist';
import { trackEvent } from 'src/utils/adapter/gobbler';

import {
  DATA_POINT_STATUS,
  DATA_TYPE,
  JOURNEY_TYPES,
  MODE_TO_SCREEN_MAP,
  ONBOARDING_FLOW_SCREENS,
  SUBMISSION_MODE,
} from '../../constants';
import { transformProgressForStore } from '../../helpers';
import { SubmissionParams } from '../../types';

import { useInitProgress } from './useInitProgress';
import { useSubmissionInit } from './useSubmissionInit';
import { useSubmissionSubmit } from './useSubmissionSubmit';
import useOnboardingStore from '../../store';
import { ValueOf } from 'src/utils/types';

export const useCosmos = ({
  handleCosmosSuccess,
  journeyType,
}: {
  handleCosmosSuccess?: () => void;
  journeyType: ValueOf<typeof JOURNEY_TYPES>;
}) => {
  const {
    setActiveScreen,
    progress,
    setProgress,
    setIsOnbDataLoading,
    setCurrentDataType,
    setCurrentSubmissionMode,
  } = useOnboardingStore();

  const initiateInitProgressFlow = useInitProgress({ journeyType });
  const initiateSubmissionInitFlow = useSubmissionInit({ journeyType });
  const initiateSubmissionSubmitFlow = useSubmissionSubmit({ journeyType });

  const handleFailure = useCallback(() => {
    setIsOnbDataLoading(false);
    setActiveScreen(ONBOARDING_FLOW_SCREENS.FAILURE);
  }, []);

  const handleSuccess = useCallback((progressData: any) => {
    const transformedProgress = transformProgressForStore(progressData);

    if (transformedProgress && Array.isArray(transformedProgress)) setProgress(transformedProgress);

    setIsOnbDataLoading(false);
  }, []);

  const mountRequiredScreen = useCallback(
    (
      dataType: ValueOf<typeof DATA_TYPE>,
      mode: ValueOf<typeof SUBMISSION_MODE>
    ) => {
      const nextScreen = MODE_TO_SCREEN_MAP[dataType]?.[mode];

      setCurrentDataType(dataType);
      setCurrentSubmissionMode(mode);
      setActiveScreen(nextScreen ?? ONBOARDING_FLOW_SCREENS.FAILURE);
    },
    []
  );


  const mountNextScreen = useCallback(() => {
    if (!progress) {
      return;
    }

    const dataPoint = progress?.find(
      ({ status }: any) => status === DATA_POINT_STATUS.NOT_SUBMITTED
    );

    if (!dataPoint) {
      handleCosmosSuccess?.();
      setIsOnbDataLoading(false);
      return;
    }

    const { modes, type } = dataPoint;
    // const rejectionReason = dataPoint?.data?.rejectionReason;
    // if (rejectionReason) {
    //   //TODO: toast.info(rejectionReason);
    // }
    
    if (!Array.isArray(modes) || modes.length === 0) {
      trackEvent('GobblerNOFWebOnbPageView', GobblerNOFWebOnbPageView, {
        dataType: `ErrorScreen_${type}`,
      });
      setActiveScreen(ONBOARDING_FLOW_SCREENS.FAILURE);
      return;
    }


    mountRequiredScreen(type as any, modes[0].type);
  }, [progress, mountRequiredScreen, handleCosmosSuccess]);

  const initiateCosmosFlow = useCallback(
    async (
      handleSuccessCb?: (resp?: any) => void,
      handleFailureCb?: (error?: any) => void
    ) => {
      setIsOnbDataLoading(true);

      const initiateCosmosSuccess = (progress: any) => {
        if (handleSuccessCb) {
          handleSuccessCb(progress);
        }
        handleSuccess(progress);
      };

      const initiateCosmosFailure = (error?: any) => {
        if (handleFailureCb) {
          handleFailureCb(error);
        }
        handleFailure();
      };

      await initiateInitProgressFlow(
        initiateCosmosSuccess,
        initiateCosmosFailure
      );
    },
    [handleFailure, handleSuccess, initiateInitProgressFlow]
  );

  const handleSubmissionInit = useCallback(
    async (submissionParams: SubmissionParams) => {
      await initiateSubmissionInitFlow(submissionParams);
    },
    [initiateSubmissionInitFlow]
  );

  const handleSubmissionSubmit = useCallback(
    async (submissionParams: SubmissionParams) => {
      await initiateSubmissionSubmitFlow(submissionParams);
    },
    [initiateSubmissionSubmitFlow]
  );

  useEffect(() => {
    mountNextScreen();
  }, [progress, mountNextScreen]);

  return {
    mountNextScreen,
    initiateCosmosFlow,
    handleSubmissionInit,
    handleSubmissionSubmit,
  };
};
